import React, { useState } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';

const BI = ({ icon, style, className = '' }) => <i className={`bi bi-${icon} ${className}`} style={style} />;
const Spin = () => <BI icon="arrow-repeat" style={{ animation: 'ats-spin .7s linear infinite', display: 'inline-block' }} />;
const LBL = { fontFamily: 'var(--erp-font-mono)', fontSize: '10px', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--erp-text-tertiary)' };

// Modale seule (exportée) : la carte candidat l'ouvre depuis son menu « Actions »
// sans avoir besoin du bouton déclencheur.
export function SkillsMatrixModal({ candidat, besoin, showNotification, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/matrix/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${APIService.getToken()}`
        },
        body: JSON.stringify({ candidat, besoin })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erreur génération');
      }

      // Télécharger le PPTX
      const blob = await res.blob();
      const score = res.headers.get('X-Matrix-Score');
      const filename = `TAT_Matrice_${candidat.nom}_${besoin.client || ''}_${new Date().toISOString().slice(0,10)}.pptx`.replace(/\s+/g, '_');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      showNotification(`Matrice générée${score ? ` — Score: ${score}%` : ''}`, 'success');
      onClose();
      // Refresh le candidat pour afficher la nouvelle pièce jointe
      if (onUpdate) setTimeout(() => onUpdate(), 1500);
    } catch (e) {
      showNotification('Erreur: ' + e.message, 'error');
    }
    setLoading(false);
  };

  return (
    <div className="erp-dialog-backdrop" style={{ zIndex: 90 }}>
      <div className="erp-dialog" role="dialog" aria-modal="true" aria-label="Matrice de compétences">
        <div className="erp-dialog__header">
          <span className="erp-dialog__icon"><BI icon="bar-chart" /></span>
          <div className="erp-dialog__heading">
            <h2>Matrice de compétences</h2>
            <p>{candidat.prenom} {candidat.nom} · {besoin.titre}</p>
          </div>
          <button type="button" className="erp-dialog__close" onClick={onClose} aria-label="Fermer">
            <BI icon="x-lg" />
          </button>
        </div>

        <div style={{ padding: '18px', display: 'flex', flexDirection: 'column', gap: '13px' }}>
          <div style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-md)', background: 'var(--erp-surface-muted)', padding: '12px 14px' }}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>L'outil va :</p>
            <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', fontSize: '12.5px', color: 'var(--erp-text-secondary)', lineHeight: 1.8 }}>
              <li><BI icon="search" style={{ marginRight: '7px', color: 'var(--erp-text-tertiary)' }} />Analyser la description du besoin</li>
              <li><BI icon="person-check" style={{ marginRight: '7px', color: 'var(--erp-text-tertiary)' }} />Évaluer le profil du candidat par domaine</li>
              <li><BI icon="easel" style={{ marginRight: '7px', color: 'var(--erp-text-tertiary)' }} />Générer un PowerPoint TAT prêt à envoyer au client</li>
            </ul>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', padding: '9px 11px' }}>
              <div style={{ ...LBL, marginBottom: '5px' }}>Candidat</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>{candidat.prenom} {candidat.nom}</div>
              <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>{candidat.experience || 0} ans exp.</div>
            </div>
            <div style={{ border: '1px solid var(--erp-line)', borderRadius: 'var(--erp-radius-sm)', padding: '9px 11px', minWidth: 0 }}>
              <div style={{ ...LBL, marginBottom: '5px' }}>Poste</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{besoin.titre}</div>
              <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>{besoin.client}</div>
            </div>
          </div>

          {!besoin.description && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 12px', border: '1px solid var(--erp-warning)', borderRadius: 'var(--erp-radius-sm)', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)', fontSize: '12px', lineHeight: 1.5 }}>
              <BI icon="exclamation-triangle" style={{ flex: 'none', marginTop: '1px' }} />
              <span>La description du besoin est vide — la matrice sera générée à partir du profil candidat uniquement. Renseigne la fiche de poste pour un meilleur résultat.</span>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px' }}>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="button" className="erp-btn erp-btn--primary" onClick={generate} disabled={loading}
              style={loading ? { opacity: 0.5 } : undefined}>
              {loading ? <><Spin /> Génération…</> : <><BI icon="download" /> Générer PPTX</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SkillsMatrixButton({ candidat, besoin, showNotification, onUpdate }) {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="bd-mini"
        title="Générer la matrice de compétences PowerPoint"
      >
        <BI icon="bar-chart" />
        Matrice
      </button>

      {showModal && (
        <SkillsMatrixModal candidat={candidat} besoin={besoin} showNotification={showNotification}
          onClose={() => setShowModal(false)} onUpdate={onUpdate} />
      )}
    </>
  );
}
